import { isAxiosError } from 'axios';
import { apiClient } from '@api';
import { PatientPhone, PhoneVerificationChannels } from '@types';

interface SendPhoneVerificationCodeParams {
  phone: PatientPhone;
  verificationChannel: PhoneVerificationChannels;
}

export async function sendPhoneVerificationCode({
  phone,
  verificationChannel,
}: SendPhoneVerificationCodeParams): Promise<void> {
  try {
    await apiClient.post('/api/v1/utilities/sendphonenumberverificationcode', {
      phone,
      verificationChannel,
    });
  } catch (error) {
    console.error('An unexpected error occurred:', error);
    throw new Error('An unexpected error occurred');
  }
}

interface ValidatePhoneNumberParams {
  countryCode?: string;
  number: string;
  prefix?: string;
}

interface ValidatePhoneNumberData {
  verificationChannels: PhoneVerificationChannels[];
}

export async function validatePhoneNumber({
  countryCode = 'US',
  number,
  prefix = '+1',
}: ValidatePhoneNumberParams): Promise<ValidatePhoneNumberData> {
  try {
    const { data } = await apiClient.post<ValidatePhoneNumberData>(
      '/api/v1/utilities/validatephonenumber',
      {
        countryCode,
        number,
        prefix,
      },
    );

    return data;
  } catch (error) {
    console.error('An unexpected error occurred:', error);
    throw new Error('An unexpected error occurred');
  }
}

interface VerifyPhoneNumberParams {
  phone: PatientPhone;
  verificationCode: string;
}

interface VerifyPhoneNumberData {
  token: string;
}

export async function verifyPhoneNumber({
  phone,
  verificationCode,
}: VerifyPhoneNumberParams): Promise<VerifyPhoneNumberData> {
  try {
    const { data } = await apiClient.post<VerifyPhoneNumberData>(
      '/api/v1/utilities/verifyphonenumber',
      {
        phone,
        verificationCode,
      },
    );

    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw error;
    }

    console.error('An unexpected error occurred', error);
    throw new Error('An unexpected error occurred');
  }
}

type WeightLossRefillsBMIValidationParams = {
  heightInches: number;
  weightPounds: number;
};

export async function weightLossRefillsBMIValidation({
  heightInches,
  weightPounds,
}: WeightLossRefillsBMIValidationParams): Promise<{
  isBodyMassIndexInRange: boolean;
}> {
  const url = `/api/v1/utilities/weightloss/bmi?HeightInches=${heightInches}&WeightPounds=${weightPounds}`;

  const { data } = await apiClient.get<{ isBodyMassIndexInRange: boolean }>(
    url,
  );

  return data;
}
